/**
 * The stock ledger: movements, balances, and the four posting shapes.
 *
 * What is absent from this file is the contract. There is no `movementPatchSchema` and no
 * delete. The ledger is append-only (invariant 1) and the database enforces it
 * independently — the application role holds only INSERT and SELECT on `stock_movements`.
 * The only correction is `POST /v1/movements/{id}/reverse`, which requires a reason.
 */
import { z } from 'zod';
import { Dec } from '../numeric/decimal.js';
import { movementTypeSchema, stockStateSchema } from './enums.js';
import { decimalIn, decimalOut, isoDateSchema, isoDateTimeSchema, nullableDecimalOut, readableSchema, uuidSchema, } from './primitives.js';
// ---------------------------------------------------------------- posting shapes
/**
 * Fields common to every posting shape.
 *
 * `batch_id` and `serial_numbers` are declared even though most items need neither: the
 * server decides which are required from the item's `tracking_policy` and refuses a
 * movement that disagrees. Omitting them from a payload type is how the typed receipt
 * route on the server once silently discarded a scanned batch — Pydantic ignores unknown
 * fields by default, and so does this schema unless the field is declared.
 */
const movementBaseShape = {
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    movement_date: isoDateSchema,
    bin_id: uuidSchema.nullish(),
    stock_state: stockStateSchema.default('AVAILABLE'),
    reference_type: z.string().max(30).nullish(),
    reference_id: uuidSchema.nullish(),
    /** The offline client's own id for this transaction, kept for reconciliation. Not the
     *  idempotency key — that travels as a header. */
    client_reference: z.string().max(64).nullish(),
    reason_code: z.string().max(40).nullish(),
    notes: z.string().nullish(),
    batch_id: uuidSchema.nullish(),
    serial_numbers: z.array(z.string().min(1)).default([]),
};
export const receiptSchema = z.object({
    ...movementBaseShape,
    quantity: decimalIn({ gt: '0', label: 'quantity' }),
    /** A per-unit rate — `Numeric(20, 4)`, not paise (D-15). Permitted only inbound. */
    unit_cost: decimalIn({ gte: '0', label: 'unit cost' }).nullish(),
});
/**
 * A goods issue. There is no `unit_cost`, and its absence is the contract.
 *
 * What an issue costs is a valuation question answered against the cost layers, not
 * something a caller may assert. Accounts accepts a caller-supplied cost on issues and
 * then silently ignores it in favour of the running average, which is worse than refusing
 * it.
 */
export const issueSchema = z.object({
    ...movementBaseShape,
    quantity: decimalIn({ gt: '0', label: 'quantity' }),
    /** Draws this issue against a specific hard hold instead of against free stock. Without
     *  it, an issue may take only `on_hand - hard`. */
    reservation_id: uuidSchema.nullish(),
});
/**
 * A correction, as a SIGNED delta.
 *
 * `delta` carries both magnitude and direction, and the server maps it to
 * ADJUSTMENT_IN or ADJUSTMENT_OUT. Assets stores adjustments as `abs(delta)` with no
 * direction at all, so a +5 and a -5 are byte-identical rows and its ledger cannot be
 * replayed. Zero is refused: an adjustment of nothing is not a correction.
 */
export const adjustmentSchema = z
    .object({
    ...movementBaseShape,
    delta: decimalIn({ label: 'adjustment' }),
})
    .refine((v) => !Dec.parse(v.delta).isZero, {
    message: 'an adjustment of zero is not a correction',
    path: ['delta'],
});
/** The canonical posting endpoint the typed shapes above are conveniences over. */
export const movementCreateSchema = z.object({
    ...movementBaseShape,
    movement_type: movementTypeSchema,
    quantity: decimalIn({ gt: '0', label: 'quantity' }),
    unit_cost: decimalIn({ gte: '0', label: 'unit cost' }).nullish(),
    reservation_id: uuidSchema.nullish(),
});
/** A reversal needs a reason, by the API and by a CHECK constraint. */
export const reversalSchema = z.object({
    reason: z.string().min(1, 'say why this movement is being reversed').max(2000),
    movement_date: isoDateSchema.nullish(),
});
// ------------------------------------------------------------------- the ledger
export const movementSchema = readableSchema.extend({
    /** `BIGINT GENERATED ALWAYS AS IDENTITY` — the only total order a same-day ledger has,
     *  since `movement_date` is a calendar day and `created_at` can collide. Sort by this. */
    seq: z.number().int(),
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    bin_id: uuidSchema.nullable().default(null),
    stock_state: stockStateSchema,
    movement_type: movementTypeSchema,
    /** +1 or -1. Constrained against `movement_type` as a pair in the database. */
    direction: z.number().int(),
    /** Always positive on the row itself; the sign lives in `direction`. */
    quantity: decimalOut,
    unit_cost: nullableDecimalOut.default(null),
    movement_date: isoDateSchema,
    posted_at: isoDateTimeSchema,
    reference_type: z.string().nullable().default(null),
    reference_id: uuidSchema.nullable().default(null),
    transfer_id: uuidSchema.nullable().default(null),
    client_reference: z.string().nullable().default(null),
    reverses_movement_id: uuidSchema.nullable().default(null),
    reason_code: z.string().nullable().default(null),
    reason: z.string().nullable().default(null),
    notes: z.string().nullable().default(null),
});
/** The contribution a row makes to a balance: quantity x direction, exactly. */
export function signedQuantity(movement) {
    return movement.direction < 0 ? movement.quantity.neg() : movement.quantity;
}
// ------------------------------------------------------------------- projections
export const balanceSchema = z.object({
    org_id: uuidSchema,
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    stock_state: stockStateSchema,
    quantity: decimalOut,
    updated_at: isoDateTimeSchema,
});
/** `GET /v1/stock/balance` returns a hand-built dict of strings rather than a DTO. */
export const singleBalanceSchema = z.object({
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    stock_state: stockStateSchema,
    quantity: decimalOut,
});
export const balanceDiscrepancySchema = z.object({
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    stock_state: stockStateSchema,
    projected: decimalOut,
    from_ledger: decimalOut,
});
/**
 * The projection audited against the ledger.
 *
 * A non-empty `discrepancies` is a defect report, not routine maintenance: the projection
 * is written in the same transaction as the movement, so it should never drift. A skin
 * showing this should say so rather than offering a cheerful "repair" button.
 */
export const rebuildResultSchema = z.object({
    rows_examined: z.number().int(),
    discrepancies: z.array(balanceDiscrepancySchema).default([]),
});
//# sourceMappingURL=stock.js.map